import * as Sentry from '@sentry/react'
import { createElement, memo, Suspense, useEffect, useState } from 'react'
import type { RouteObject } from 'react-router'
import {
  isRouteErrorResponse,
  matchPath,
  Outlet,
  useLocation,
  useRouteError,
} from 'react-router'
import { ContentContainer, SButton, SNavLink, Spinner } from 'tailwind-ui'
import Description from 'tailwind-ui/typeography/description'
import Heading from 'tailwind-ui/typeography/heading'
import { newRoutesArray } from '@/paths'
import { RouteGuard } from '@/access/gate'
import type { NormalRoutesType } from './types'
import { isInStandaloneMode } from './utils/helpers/pwa'
import BubbleLoad from './ux/bubble-load'

export function ErrorBoundary() {
  const error = useRouteError()
  const location = useLocation()
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
    }
    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const isChunkError =
    error.toString().includes('ChunkLoad') ||
    error.toString().includes('Loading CSS chunk') ||
    error.toString().includes('Failed to fetch dynamically imported module') ||
    error.toString().includes(`'text/html' is not a valid JavaScript MIME`)

  // Handle offline state
  if (!isOnline) {
    return (
      <ContentContainer className="py-4 space-y-4">
        <div>
          <Heading>You're offline</Heading>
          <Description>
            Please check your internet connection and try again when you're back
            online.
          </Description>
        </div>
        <div className="rounded-full gap-1 flex p-1">
          <SButton
            onClick={() => {
              window.location.reload()
            }}
          >
            Try Again
          </SButton>
        </div>
      </ContentContainer>
    )
  }

  if (isRouteErrorResponse(error) && error.status === 401) {
    // the response json is automatically parsed to
    // `error.data`, you also have access to the status
    return (
      <div>
        <h1>{error.status}</h1>
        <h2>{error.data.sorry}</h2>
        <p>{error.data.hrEmail} if this is a mistake.</p>
      </div>
    )
  }

  //if (isRouteErrorResponse(error) && error.status === 404) {

  if (isChunkError) {
    // Stale chunk after a deploy — reload to fetch the new index.html + hashed
    // chunks. Guard against a reload loop (e.g. CDN still serving the old
    // index): only auto-reload if we haven't already done so in the last 10s.
    const lastReload = Number(
      sessionStorage.getItem('chunk-error-reload-at') ?? 0
    )
    if (Date.now() - lastReload > 10_000) {
      sessionStorage.setItem('chunk-error-reload-at', String(Date.now()))
      window.location.reload()
      return <BubbleLoad text="Reloading page..." />
    }
  }
  Sentry.captureException(error)

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  return (
    <ContentContainer className="py-4 space-y-4">
      <div>
        <Heading>Something went wrong</Heading>
        <Description>
          Something went wrong loading this section. Please try reloading the
          page
        </Description>
      </div>
      {import.meta.env.DEV ? (
        <div className="h-full rounded-xl gap-1 flex p-4 bg-red-500 border border-black/10 dark:border-white/10 flex-col">
          <p className="text-lg">{error.toString()}</p>
          <code className="text-xs">
            <pre>{JSON.stringify(location, null, 2)}</pre>
          </code>
        </div>
      ) : null}
      <div className="rounded-full gap-1 flex p-1">
        <SButton
          onClick={() => {
            window.location.reload()
          }}
        >
          Reload Page
        </SButton>

        {isInStandaloneMode() ? (
          <SButton
            variant="ghost_default"
            onClick={() => {
              navigator.serviceWorker
                .getRegistrations()
                .then(async (registrations: any) => {
                  await registrations.forEach((registration: any) =>
                    registration.unregister()
                  )
                  window.location.reload()
                })
            }}
          >
            Reload App
          </SButton>
        ) : (
          <SNavLink to="/" variant="ghost_default">
            Reload App
          </SNavLink>
        )}
      </div>
    </ContentContainer>
  )
}

const RoutesToItem = (routes: NormalRoutesType[]): RouteObject[] => {
  return routes.map((route): RouteObject => {
    const routeObject: RouteObject = {
      path: route.path,
      errorElement: <ErrorBoundary />,
      index: route.index,
    }

    if (route.component) {
      if (typeof route.component === 'string') {
        console.warn('String components are not supported:', route.component)
      } else if (route.component.$$typeof.toString() === 'Symbol(react.lazy)') {
        routeObject.element = (
          <Suspense
            fallback={route.fallback ?? <BubbleLoad text="Loading..." />}
          >
            {createElement(route.component)}
          </Suspense>
        )
      } else {
        routeObject.element = route.component
      }
    }

    // Access gate. A route that declares a `capability` is enforced here, so it
    // holds however the user got there — nav click, bookmark or typed URL —
    // rather than depending on the sidebar having hidden the link.
    //
    // A pathless container route (children, no component of its own) gets a
    // guarded `<Outlet />`, so putting the capability on a section's parent
    // covers the whole subtree instead of needing it on every leaf.
    if (route.capability) {
      routeObject.element = (
        <RouteGuard
          capability={route.capability}
          redirectTo={route.capabilityRedirectTo}
        >
          {routeObject.element ?? <Outlet />}
        </RouteGuard>
      )
    }

    // Handle nested routes recursively
    if (route.children && route.children.length > 0) {
      routeObject.children = RoutesToItem(route.children)
    }

    // Handle any additional route metadata/handles
    if (route.handle) {
      routeObject.handle = route.handle
    }

    return routeObject
  })
}

export const routes = RoutesToItem(newRoutesArray)

/* ───────────────────────────────────────────────────────────────
 *  Route chunk prefetching
 *
 *  Every route is code-split (`lazyWithPreload`), so the first visit to a
 *  view waits on its JS chunk — which is what makes a freshly-clicked
 *  sub-nav item feel like it "takes ages". We flatten the route tree into
 *  { full path pattern → preload() } so the nav can fetch a chunk ahead of
 *  the click (on hover / when a section's sub-menu opens). Best-effort: a
 *  miss or failure just falls back to the normal on-navigation load.
 * ─────────────────────────────────────────────────────────────── */
interface PreloadEntry {
  pattern: string
  preload: () => Promise<unknown>
}

function joinPath(parent: string, segment: string): string {
  if (!segment) return parent || '/'
  if (segment.startsWith('/')) return segment
  return `${parent.replace(/\/$/, '')}/${segment}`
}

function collectPreloads(
  list: NormalRoutesType[],
  parent: string,
  out: PreloadEntry[]
): void {
  for (const route of list) {
    const full = route.index ? parent : joinPath(parent, route.path ?? '')
    const component = route.component as unknown as {
      preload?: () => Promise<unknown>
    } | null
    if (component && typeof component.preload === 'function') {
      out.push({ pattern: full || '/', preload: component.preload })
    }
    if (route.children?.length) collectPreloads(route.children, full, out)
  }
}

const preloadEntries: PreloadEntry[] = []
collectPreloads(newRoutesArray, '', preloadEntries)

/** Prefetch the code-split chunk(s) for whatever route an href resolves to. */
export function preloadRouteByHref(href: string | undefined): void {
  if (!href) return
  const pathname = href.split('?')[0].split('#')[0]
  for (const entry of preloadEntries) {
    if (matchPath(entry.pattern, pathname)) {
      // Swallow prefetch failures — a stale chunk (post-deploy) or offline
      // blip here is harmless: the real navigation will retry and the route
      // error boundary handles the reload. Without this, the rejected preload
      // surfaces as an "Uncaught (in promise) Failed to fetch dynamically
      // imported module" in the console on hover.
      entry.preload().catch(() => {})
      return
    }
  }
}
